import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchDevices, registerDevice, toggleDeviceActive, revokeDevice } from '../services/api';
import { useSuperTenant } from '../context/SuperAdminTenantContext';
import type { Device } from '../types';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

const DevicesPage: React.FC = () => {
  const { user } = useAuth();
  const { tenantId } = useSuperTenant();
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [imei, setImei] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [revoking, setRevoking] = useState<number | null>(null);
  const [toggling, setToggling] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    fetchDevices({ tenant_id: tenantId || undefined })
      .then(setDevices)
      .catch(() => setError('Failed to load devices'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await registerDevice({ device_name: deviceName || undefined, phone_imei: imei || undefined });
      setShowModal(false);
      setDeviceName('');
      setImei('');
      load();
    } catch {
      setError('Failed to register device. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id: number) => {
    setToggling(id);
    try {
      const res = await toggleDeviceActive(id);
      setDevices(prev => prev.map(d => d.id === id ? { ...d, is_active: res.is_active } : d));
    } catch {
      setError('Failed to update device status.');
    } finally {
      setToggling(null);
    }
  };

  const handleRevoke = async (id: number) => {
    if (!window.confirm('Revoke this device? It will no longer be able to upload calls.')) return;
    setRevoking(id);
    try {
      await revokeDevice(id);
      setDevices(prev => prev.filter(d => d.id !== id));
    } catch {
      setError('Failed to revoke device.');
    } finally {
      setRevoking(null);
    }
  };

  const apiUrl = (process.env.REACT_APP_API_URL ?? 'http://localhost:8001/api').replace('/api', '');

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
            Mobile Dialer Devices
          </h1>
          <p style={{ marginTop: 6, color: 'var(--text-muted)', fontSize: 14 }}>
            Registered Android devices that upload SIM call recordings to the CRM.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          + Register Device
        </button>
      </div>

      {error && (
        <div style={{
          background: '#fee2e2', color: '#dc2626', borderRadius: 8,
          padding: '10px 16px', marginBottom: 16, fontSize: 14,
        }}>
          {error}
        </div>
      )}

      {/* Setup instructions card */}
      <div style={{
        background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10,
        padding: '16px 20px', marginBottom: 24,
      }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#0369a1', marginBottom: 8 }}>
          How to link the Android app
        </div>
        <ol style={{ margin: 0, paddingLeft: 20, color: '#0c4a6e', fontSize: 13, lineHeight: 1.9 }}>
          <li>Install the Automately Dialer app on a rooted Android device.</li>
          <li>Open the app → Settings → Link CRM Account.</li>
          <li>
            Enter: <strong>Tenant Code</strong> = <code style={{ background: '#e0f2fe', padding: '1px 6px', borderRadius: 4 }}>1</code>
            &nbsp;&nbsp;<strong>Agent ID</strong> = <code style={{ background: '#e0f2fe', padding: '1px 6px', borderRadius: 4 }}>{user?.id}</code>
            &nbsp;&nbsp;<strong>API URL</strong> = <code style={{ background: '#e0f2fe', padding: '1px 6px', borderRadius: 4 }}>{apiUrl}</code>
          </li>
          <li>Tap Register — the device will appear in the list below.</li>
        </ol>
      </div>

      {/* Devices table */}
      {loading ? (
        <div className="spinner">Loading…</div>
      ) : devices.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '48px 0',
          color: 'var(--text-muted)', fontSize: 14,
          border: '1px dashed var(--border)', borderRadius: 10,
        }}>
          No devices registered yet. Register one to start capturing SIM call recordings.
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                {['Device', 'Agent', 'IMEI', 'Status', 'Registered', 'Last Seen', ''].map(h => (
                  <th key={h} style={{
                    padding: '10px 16px', textAlign: 'left',
                    fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {devices.map((d, i) => (
                <tr key={d.id} style={{
                  borderBottom: i < devices.length - 1 ? '1px solid var(--border)' : 'none',
                  background: '#fff',
                }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>
                      {d.device_name ?? <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>Unnamed device</span>}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text)' }}>
                    {d.user?.name ?? `User #${d.user_id}`}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                    {d.phone_imei ? `••••${d.phone_imei.slice(-4)}` : '—'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <button
                      onClick={() => handleToggle(d.id)}
                      disabled={toggling === d.id}
                      title={d.is_active ? 'Click to deactivate' : 'Click to activate'}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        border: 'none', cursor: toggling === d.id ? 'default' : 'pointer',
                        background: d.is_active ? '#dcfce7' : '#fee2e2',
                        color:      d.is_active ? '#166534' : '#991b1b',
                        opacity: toggling === d.id ? 0.6 : 1,
                        transition: 'all 0.15s',
                      }}
                    >
                      <span style={{
                        width: 7, height: 7, borderRadius: '50%',
                        background: d.is_active ? '#16a34a' : '#dc2626',
                        display: 'inline-block',
                      }} />
                      {toggling === d.id ? '…' : d.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)' }}>
                    {fmtDate(d.registered_at)}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13 }}>
                    {d.last_seen_at ? (
                      <span style={{ color: '#16a34a' }}>{timeAgo(d.last_seen_at)}</span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>Never</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    {(user?.role === 'tenant_admin' || user?.role === 'super_admin' || user?.id === d.user_id) && (
                      <button
                        onClick={() => handleRevoke(d.id)}
                        disabled={revoking === d.id}
                        style={{
                          background: 'none', border: '1px solid #fca5a5',
                          color: '#dc2626', borderRadius: 6, padding: '4px 12px',
                          fontSize: 12, cursor: 'pointer',
                        }}
                      >
                        {revoking === d.id ? 'Revoking…' : 'Revoke'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Register modal */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}
          onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div style={{
            background: '#fff', borderRadius: 12, padding: '28px 32px',
            width: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: 'var(--navy)' }}>
              Register Device
            </h2>
            <form onSubmit={handleRegister}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                  Device Name
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Samsung Galaxy S21"
                  value={deviceName}
                  onChange={e => setDeviceName(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                  IMEI <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="15-digit IMEI"
                  value={imei}
                  onChange={e => setImei(e.target.value)}
                  maxLength={15}
                  style={{ width: '100%' }}
                />
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  Dial *#06# on the device to find the IMEI.
                </div>
              </div>
              {error && (
                <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { setShowModal(false); setError(''); }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Registering…' : 'Register'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevicesPage;
